'use client';

import { useEffect, useState, type MouseEvent } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  id: string;
  label: string;
  href: string;
  offset: number;
}

interface StickyNavigationProps {
  onWhatsAppClick?: () => void;
}

/**
 * ✅ ORDEN DEL MENÚ
 * 1. Inicio
 * 2. Sobre mí
 * 3. Servicios
 * 4. Planes
 * 5. Equipo
 * 6. Ubicación
 */
const navigationItems: NavigationItem[] = [
  { id: 'inicio', label: 'Inicio', href: '/', offset: 0 },
  { id: 'sobre-mi', label: 'Sobre mí', href: '/sobre-mi', offset: 80 },
  { id: 'servicios', label: 'Servicios', href: '/servicios', offset: 80 },
  { id: 'planes', label: 'Planes', href: '/planes', offset: 80 },
  { id: 'equipo', label: 'Equipo', href: '/equipo', offset: 80 },
  { id: 'ubicacion', label: 'Ubicación', href: '/ubicacion', offset: 80 },
];

// Paths que representan la landing (la home y cada sección con URL limpia).
// Sirve para saber si estamos "en la página que scrollea".
const SECTION_PATHS = new Set(navigationItems.map((item) => `/${item.id}`));
const isLandingPath = (path: string) =>
  path === '/' || path === '' || SECTION_PATHS.has(path);

// URL limpia de cada sección: inicio = "/", el resto = "/<id>".
const pathForSection = (id: string) => (id === 'inicio' ? '/' : `/${id}`);

// Portal / app de atletas
const APP_URL = 'https://app.victorcuellar.fit/';

const StickyNavigation = ({}: StickyNavigationProps) => {
  const [activeSection, setActiveSection] = useState<string>('inicio');
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
    const NAV_HEIGHT = 80;
    const ACTIVE_MARGIN = 12;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      if (isLandingPath(window.location.pathname)) {
        const scrollPosition = window.scrollY + NAV_HEIGHT + ACTIVE_MARGIN;

        for (let i = navigationItems.length - 1; i >= 0; i--) {
          const id = navigationItems[i].id;
          const el = document.getElementById(id);
          if (el && el.offsetTop <= scrollPosition) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: MouseEvent<HTMLAnchorElement>, item: NavigationItem) => {
    if (isLandingPath(window.location.pathname)) {
      e.preventDefault();
      setIsMenuOpen(false);

      const element = document.getElementById(item.id);
      if (element) {
        const offsetPosition = element.offsetTop - item.offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        window.history.pushState(null, '', pathForSection(item.id));
        setActiveSection(item.id);
      }
    } else {
      setIsMenuOpen(false);
    }

    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'navigation_click', {
        section: item.id,
        label: item.label,
      });
    }
  };

  const handleAppClick = () => {
    window.open(APP_URL, '_blank', 'noopener,noreferrer');

    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'app_login_click', {
        location: 'navigation',
        section: activeSection,
      });
    }
  };

  // Bloquea el scroll del body y permite cerrar el menú con Escape (UX pro en mobile)
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <>
      <nav data-component-id="src/components/common/StickyNavigation.tsx:144:6" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="144" data-component-end-line="277" data-component-file="StickyNavigation.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
        className={`fixed top-0 left-0 right-0 z-[100] border-b transition-all duration-300 ${
          isScrolled ? 'bg-card border-white/10 shadow-card' : 'bg-card border-white/5'
        }`}
      >
        <div data-component-id="src/components/common/StickyNavigation.tsx:149:8" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="149" data-component-end-line="219" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/components/common/StickyNavigation.tsx:150:10" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="150" data-component-end-line="218" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-20%22%7D" className="flex items-center justify-between h-20">
            {/* LOGO OFICIAL */}
            <Link data-component-id="src/components/common/StickyNavigation.tsx:152:12" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="152" data-component-end-line="168" data-component-file="StickyNavigation.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22flex%20items-center%20group%20shrink-0%22%7D"
              href="/"
              onClick={(e) => handleNavClick(e, navigationItems[0])}
              className="flex items-center group shrink-0"
              aria-label="Ir a inicio"
            >
              <div data-component-id="src/components/common/StickyNavigation.tsx:158:14" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="158" data-component-end-line="167" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20h-10%20w-%5B132px%5D%20sm%3Aw-%5B160px%5D%22%7D" className="relative h-10 w-[132px] sm:w-[160px]">
                <Image data-component-id="src/components/common/StickyNavigation.tsx:159:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="159" data-component-end-line="166" data-component-file="StickyNavigation.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fassets%2Fimages%2Fvictor-cuellar-logo.png%22%2C%22alt%22%3A%22Victor%20Cuellar%20%E2%80%93%20Preparaci%C3%B3n%20F%C3%ADsica%20Aplicada%20al%20Rendimiento%22%2C%22className%22%3A%22object-contain%20object-left%20transition-transform%20duration-250%20group-hover%3Ascale-%5B1.02%5D%22%7D"
                  src="/assets/images/victor-cuellar-logo.png"
                  alt="Victor Cuellar – Preparación Física Aplicada al Rendimiento"
                  fill
                  priority
                  sizes="(max-width: 640px) 132px, 160px"
                  className="object-contain object-left transition-transform duration-250 group-hover:scale-[1.02]"
                />
              </div>
            </Link>

            {/* NAV DESKTOP */}
            <div data-component-id="src/components/common/StickyNavigation.tsx:171:12" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="171" data-component-end-line="188" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20space-x-1%22%7D" className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link data-component-id="src/components/common/StickyNavigation.tsx:173:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="173" data-component-end-line="186" data-component-file="StickyNavigation.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`px-4 py-2 rounded-lg font-body font-semibold text-sm transition-all duration-250 ${
                    mounted &&
                    activeSection === item.id &&
                    isLandingPath(window.location.pathname)
                      ? 'text-primary bg-primary/10 border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-white hover:bg-muted'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* CONTROLES DERECHA */}
            <div data-component-id="src/components/common/StickyNavigation.tsx:191:12" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="191" data-component-end-line="217" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2.5%20sm%3Agap-3%20shrink-0%22%7D" className="flex items-center gap-2.5 sm:gap-3 shrink-0">
              <button data-component-id="src/components/common/StickyNavigation.tsx:192:14" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="192" data-component-end-line="205" data-component-file="StickyNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={handleAppClick}
                className={`group items-center gap-2 rounded-lg bg-primary px-3.5 sm:px-5 py-2.5 font-cta font-bold text-sm text-white shadow-[0_2px_12px_rgba(225,6,0,0.25)] transition-all duration-200 hover:bg-primary/90 hover:shadow-[0_6px_22px_rgba(225,6,0,0.45)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-card ${
                  isMenuOpen ? 'hidden lg:inline-flex' : 'inline-flex'
                }`}
              >
                <Icon data-component-id="src/components/common/StickyNavigation.tsx:198:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="198" data-component-end-line="203" data-component-file="StickyNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22BoltIcon%22%2C%22className%22%3A%22transition-transform%20duration-200%20group-hover%3Ascale-110%22%7D"
                  name="BoltIcon"
                  size={18}
                  variant="solid"
                  className="transition-transform duration-200 group-hover:scale-110"
                />
                <span data-component-id="src/components/common/StickyNavigation.tsx:204:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="204" data-component-end-line="204" data-component-file="StickyNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Ingresar%22%7D">Ingresar</span>
              </button>

              {/* BOTÓN MENÚ (mobile) */}
              <button data-component-id="src/components/common/StickyNavigation.tsx:208:14" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="208" data-component-end-line="216" data-component-file="StickyNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20inline-flex%20items-center%20justify-center%20w-11%20h-11%20rounded-lg%20border%20border-white%2F10%20bg-white%2F5%20text-white%20transition-colors%20duration-200%20hover%3Abg-white%2F10%20hover%3Aborder-white%2F20%20focus%3Aoutline-none%20focus-visible%3Aring-2%20focus-visible%3Aring-primary%2F60%22%7D"
                onClick={() => setIsMenuOpen((v) => !v)}
                className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg border border-white/10 bg-white/5 text-white transition-colors duration-200 hover:bg-white/10 hover:border-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                <Icon data-component-id="src/components/common/StickyNavigation.tsx:215:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="215" data-component-end-line="215" data-component-file="StickyNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
              </button>
            </div>
          </div>
        </div>

        {/* PANEL MENÚ MOBILE */}
        <div data-component-id="src/components/common/StickyNavigation.tsx:222:8" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="222" data-component-end-line="276" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22mobile-menu%22%7D"
          id="mobile-menu"
          className={`lg:hidden absolute top-full inset-x-0 origin-top transition-all duration-300 ease-out ${
            isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
          }`}
        >
          <div data-component-id="src/components/common/StickyNavigation.tsx:228:10" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="228" data-component-end-line="275" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-4%20mt-2%20overflow-hidden%20rounded-2xl%20border%20border-white%2F10%20bg-card%20shadow-card%22%7D" className="mx-4 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-card shadow-card">
            <div data-component-id="src/components/common/StickyNavigation.tsx:229:12" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="229" data-component-end-line="259" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-2%22%7D" className="p-2">
              {navigationItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <Link data-component-id="src/components/common/StickyNavigation.tsx:233:18" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="233" data-component-end-line="256" data-component-file="StickyNavigation.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                    key={item.id}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`group flex items-center justify-between rounded-xl px-4 py-3.5 font-body font-semibold text-[15px] transition-colors duration-200 ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <span data-component-id="src/components/common/StickyNavigation.tsx:243:20" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="243" data-component-end-line="250" data-component-file="StickyNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
                      <span data-component-id="src/components/common/StickyNavigation.tsx:244:22" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="244" data-component-end-line="248" data-component-file="StickyNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                        className={`h-5 w-1 rounded-full transition-colors ${
                          isActive ? 'bg-primary' : 'bg-transparent group-hover:bg-white/20'
                        }`}
                      />
                      {item.label}
                    </span>
                    <Icon data-component-id="src/components/common/StickyNavigation.tsx:251:20" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="251" data-component-end-line="255" data-component-file="StickyNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%7D"
                      name="ChevronRightIcon"
                      size={16}
                      className={isActive ? 'text-primary' : 'text-white/25'}
                    />
                  </Link>
                );
              })}
            </div>

            <div data-component-id="src/components/common/StickyNavigation.tsx:261:12" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="261" data-component-end-line="274" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-white%2F10%20p-3%22%7D" className="border-t border-white/10 p-3">
              <button data-component-id="src/components/common/StickyNavigation.tsx:262:14" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="262" data-component-end-line="273" data-component-file="StickyNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22group%20flex%20w-full%20items-center%20justify-center%20gap-2%20rounded-xl%20bg-primary%20px-6%20py-3.5%20font-cta%20font-bold%20text-sm%20text-white%20shadow-%5B0_2px_12px_rgba(225%2C6%2C0%2C0.25)%5D%20transition-all%20duration-200%20hover%3Abg-primary%2F90%20active%3Ascale-%5B0.98%5D%22%7D"
                onClick={handleAppClick}
                className="group flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 font-cta font-bold text-sm text-white shadow-[0_2px_12px_rgba(225,6,0,0.25)] transition-all duration-200 hover:bg-primary/90 active:scale-[0.98]"
              >
                <Icon data-component-id="src/components/common/StickyNavigation.tsx:266:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="266" data-component-end-line="271" data-component-file="StickyNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22BoltIcon%22%2C%22className%22%3A%22transition-transform%20duration-200%20group-hover%3Ascale-110%22%7D"
                  name="BoltIcon"
                  size={18}
                  variant="solid"
                  className="transition-transform duration-200 group-hover:scale-110"
                />
                <span data-component-id="src/components/common/StickyNavigation.tsx:272:16" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="272" data-component-end-line="272" data-component-file="StickyNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Ingresar%20a%20la%20App%22%7D">Ingresar a la App</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* BACKDROP */}
      <div data-component-id="src/components/common/StickyNavigation.tsx:280:6" data-component-path="src/components/common/StickyNavigation.tsx" data-component-line="280" data-component-end-line="286" data-component-file="StickyNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`lg:hidden fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
    </>
  );
};

export default StickyNavigation;